import { graphql } from '@@fire-b2b/graphql/codegen'
import type { Brand } from '@@fire-b2b/types/Brand'
import { DEFAULT_B2B_CONFIGURATION } from '@@fire-b2b/types/ClientConfiguration'
import type { HexColorString } from '@@fire-base/types/utils/ColorString'
import { gqlExecute, type GQLResult } from '@fire-b2b/utils/gql'
import { fetchBrandsConfigs } from './fetchBrandsConfigs'

export async function fetchBrands(): Promise<GQLResult<Brand[]>> {
  const brandsPromise = await gqlExecute({
    query: graphql(`
      query ShopClients($brandImageWidth: Int!, $landingPageHeroImageWidth: Int!) {
        clients {
          totalCount
          results {
            id
            shopClientId
            title
            brandColor
            fallbackColor: color
            status
            sortOrder
            logoHeader: medias(targetAttr: "b2b_logo_header") {
              path
              alt
            }
            logoFooter: medias(targetAttr: "b2b_logo_footer") {
              path
              alt
            }
            brandImage: medias(targetAttr: "image") {
              thumbnail(width: $brandImageWidth)
              alt
            }
            brandLogo: medias(targetAttr: "logo") {
              path
              alt
            }
            landingpagePreOrderHeroImages: medias(targetAttr: "pre_order_hero_images") {
              alt
              label
              link
              linkTitle
              sortOrder
              thumbnail(width: $landingPageHeroImageWidth)
            }
            landingpagePreOrderHeroImagesMobile: medias(targetAttr: "pre_order_hero_images_mobile") {
              alt
              label
              link
              linkTitle
              sortOrder
              thumbnail(width: $landingPageHeroImageWidth)
            }
            landingpageReOrderHeroImages: medias(targetAttr: "re_order_hero_images") {
              alt
              label
              link
              linkTitle
              sortOrder
              thumbnail(width: $landingPageHeroImageWidth)
            }
            landingpageReOrderHeroImagesMobile: medias(targetAttr: "re_order_hero_images_mobile") {
              alt
              label
              link
              linkTitle
              sortOrder
              thumbnail(width: $landingPageHeroImageWidth)
            }
          }
        }
      }
    `),
    variables: {
      brandImageWidth: 800,
      landingPageHeroImageWidth: 2000,
    },
  })

  const brandsConfigsPromise = fetchBrandsConfigs()

  const [brandsResult, brandsConfigsResult] = await Promise.all([brandsPromise, brandsConfigsPromise])

  if (isErr(brandsResult)) {
    return brandsResult
  }
  if (isErr(brandsConfigsResult)) {
    return brandsConfigsResult
  }

  const brands: Brand[] = []

  for (const client of brandsResult.data.clients?.results ?? []) {
    if (!client.id || !client.shopClientId || !client.title || !client.status) {
      console.warn(`Skipping client with missing required fields:`, {
        id: client.id,
        shopClientId: client.shopClientId,
        title: client.title,
        status: client.status,
      })
      continue
    }

    let brandConfig = brandsConfigsResult.data.find((config) => config.shopClientId === client.shopClientId)
    if (!brandConfig) {
      console.warn(`No brand config found for client ${client.shopClientId}, using default configuration.`)
      brandConfig = {
        shopClientId: client.shopClientId,
        hasPreOrder: false,
        hasReOrder: false,
        hasReOrderDirect: false,
        preOrderSplitSeason: false,
        reOrderSplitSeason: false,
        preOrderSplitProductline: false,
        reOrderSplitProductline: false,
        preOrderBlock: false,
        preOrderBlockStartDate: null,
        preOrderBlockEndDate: null,
        preOrderSeasonTitle: null,
        b2bConfiguration: DEFAULT_B2B_CONFIGURATION,
      }
    }

    const brand: Brand = {
      id: client.id,
      shopClientId: client.shopClientId,
      sortOrder: client.sortOrder ?? 0,
      title: client.title,
      color: ((client.brandColor || client.fallbackColor) as HexColorString) ?? '#000000',
      hasPreOrder: brandConfig.hasPreOrder,
      hasReOrder: brandConfig.hasReOrder,
      hasReOrderDirect: brandConfig.hasReOrderDirect,
      preOrderSplitSeason: brandConfig.preOrderSplitSeason,
      reOrderSplitSeason: brandConfig.reOrderSplitSeason,
      preOrderSplitProductline: brandConfig.preOrderSplitProductline,
      reOrderSplitProductline: brandConfig.reOrderSplitProductline,
      preOrderBlock: brandConfig.preOrderBlock,
      preOrderBlockStartDate: brandConfig.preOrderBlockStartDate,
      preOrderBlockEndDate: brandConfig.preOrderBlockEndDate,
      preOrderSeasonTitle: brandConfig.preOrderSeasonTitle,
      b2bConfiguration: brandConfig.b2bConfiguration,
      logoHeader: client.logoHeader.map((media) => {
        return {
          path: media.path ?? '',
          alt: media.alt ?? '',
        }
      }),
      logoFooter: client.logoFooter.map((media) => {
        return {
          path: media.path ?? '',
          alt: media.alt ?? '',
        }
      }),
      brandImage: client.brandImage.map((media) => {
        return {
          thumbnail: media.thumbnail ?? '',
          alt: media.alt ?? '',
        }
      }),
      brandLogo: client.brandLogo.map((media) => {
        return {
          path: media.path ?? '',
          alt: media.alt ?? '',
        }
      }),
      landingPagePreOrderHeroImages: client.landingpagePreOrderHeroImages.map((media) => {
        return {
          thumbnail: media.thumbnail ?? '',
          alt: media.alt ?? '',
          label: media.label ?? '',
          link: media.link ?? null,
          linkTitle: media.linkTitle ?? null,
        }
      }),
      landingPagePreOrderHeroImagesMobile: client.landingpagePreOrderHeroImagesMobile.map((media) => {
        return {
          thumbnail: media.thumbnail ?? '',
          alt: media.alt ?? '',
          label: media.label ?? '',
          link: media.link ?? null,
          linkTitle: media.linkTitle ?? null,
        }
      }),
      landingPageReOrderHeroImages: client.landingpageReOrderHeroImages.map((media) => {
        return {
          thumbnail: media.thumbnail ?? '',
          alt: media.alt ?? '',
          label: media.label ?? '',
          link: media.link ?? null,
          linkTitle: media.linkTitle ?? null,
        }
      }),
      landingPageReOrderHeroImagesMobile: client.landingpageReOrderHeroImagesMobile.map((media) => {
        return {
          thumbnail: media.thumbnail ?? '',
          alt: media.alt ?? '',
          label: media.label ?? '',
          link: media.link ?? null,
          linkTitle: media.linkTitle ?? null,
        }
      }),
    }

    brands.push(brand)
  }

  brands.sort((a, b) => a.sortOrder - b.sortOrder)

  return ok(brands)
}
